import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import PageSeo from '../components/seo/PageSeo';

// ─── Conteúdo completo da política (fallback estático) ───────────────────────
const STATIC_POLICY = {
  version: '2.1',
  last_updated: '2026-07-21T00:00:00.000Z',
};

// ─── Seções da política ───────────────────────────────────────────────────────
const sections = [
  {
    id: 'introducao',
    icon: '📋',
    title: '1. Introdução e Identificação do Controlador',
    content: [
      {
        type: 'text',
        value:
          'Esta Política de Privacidade descreve como a plataforma Ufokko coleta, usa, armazena e protege seus dados pessoais, em total conformidade com a Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018) e com as demais legislações aplicáveis.',
      },
      {
        type: 'text',
        value:
          'A Ufokko é uma plataforma brasileira de contratação de videomakers, que conecta em tempo real clientes que precisam de serviços audiovisuais a profissionais qualificados. Nossa plataforma opera via aplicativo móvel (iOS e Android) e site (ufokko.com.br).',
      },
      {
        type: 'info',
        label: 'Controlador de Dados',
        items: [
          'Nome: Ufokko Serviços Digitais Ltda.',
          'Email de contato: contato@ufokko.com.br',
          'DPO (Encarregado de Dados): dpo@ufokko.com.br',
          'Localização: Rio de Janeiro, RJ, Brasil',
        ],
      },
    ],
  },
  {
    id: 'dados-coletados',
    icon: '🗂️',
    title: '2. Dados Pessoais Coletados',
    content: [
      {
        type: 'text',
        value:
          'Coletamos diferentes categorias de dados conforme o tipo de usuário e o uso da plataforma:',
      },
      {
        type: 'subsection',
        label: '2.1 Dados Cadastrais (todos os usuários)',
        items: [
          'Nome completo',
          'Endereço de e-mail',
          'Número de telefone',
          'Senha (armazenada com hash bcrypt — nunca em texto claro)',
          'Foto de perfil (armazenada na AWS S3)',
          'Cidade e estado de atuação',
          'Tipo de conta (cliente ou videomaker)',
          'Identificador Apple (ao usar Sign in with Apple)',
          'Data e hora de cadastro',
        ],
      },
      {
        type: 'subsection',
        label: '2.2 Dados de Videomakers (profissionais)',
        items: [
          'Lista de equipamentos e especializações',
          'Portfólio de trabalhos (vídeos e imagens armazenados na AWS S3)',
          'Área de cobertura geográfica (raio de atendimento)',
          'Status de disponibilidade em tempo real',
          'Dados bancários e informações da conta Stripe Connect (para recebimento de pagamentos)',
          'Histórico de jobs realizados, avaliações e nota média',
        ],
      },
      {
        type: 'subsection',
        label: '2.3 Dados de Localização',
        items: [
          'Coordenadas GPS para matching em tempo real (cliente e videomaker próximos)',
          'Endereço específico do local do serviço informado no job',
          'Localização somente é capturada com permissão explícita do usuário',
        ],
      },
      {
        type: 'subsection',
        label: '2.4 Dados de Pagamento',
        items: [
          'Transações processadas via Stripe (não armazenamos dados de cartão diretamente)',
          'IDs de PaymentIntent e Transfer (referências Stripe)',
          'Valor dos jobs e status de pagamento (pendente, capturado, liberado)',
          'Dados de conta Stripe Connect do videomaker (validados pela Stripe)',
        ],
      },
      {
        type: 'subsection',
        label: '2.5 Dados de Jobs e Entregas',
        items: [
          'Tipo de serviço contratado, data, horário e localização do job',
          'Valor negociado e status do job',
          'Mensagens de chat entre cliente e videomaker',
          'Arquivos de entrega (vídeos/imagens enviados pelo videomaker — armazenados na AWS S3)',
          'Status de aprovação, revisões solicitadas e avaliações pós-serviço',
        ],
      },
      {
        type: 'subsection',
        label: '2.6 Dados Técnicos e de Uso',
        items: [
          'Tokens JWT de autenticação (expiram automaticamente)',
          'Logs de acesso e auditoria (com informações de identificação parcialmente mascaradas)',
          'Tipo de dispositivo e sistema operacional (para diagnóstico)',
          'Dados de conexão WebSocket para comunicação em tempo real',
        ],
      },
    ],
  },
  {
    id: 'finalidade',
    icon: '🎯',
    title: '3. Finalidade e Base Legal',
    content: [
      {
        type: 'text',
        value:
          'Cada tratamento de dados possui uma finalidade específica e uma base legal prevista na LGPD:',
      },
      {
        type: 'table',
        rows: [
          ['Finalidade', 'Base Legal (LGPD)'],
          ['Criação e gestão de conta', 'Execução de contrato (Art. 7º, V)'],
          ['Matching em tempo real (GPS)', 'Execução de contrato / Consentimento (Art. 7º, I e V)'],
          ['Processamento de pagamentos via escrow', 'Execução de contrato (Art. 7º, V)'],
          ['Comunicação entre usuários (chat)', 'Execução de contrato (Art. 7º, V)'],
          ['Armazenamento de entregas (S3)', 'Execução de contrato (Art. 7º, V)'],
          ['Processamento de vídeos (MediaConvert)', 'Execução de contrato (Art. 7º, V)'],
          ['Envio de notificações do serviço', 'Legítimo interesse / Consentimento (Art. 7º, IX e I)'],
          ['Logs de auditoria e segurança', 'Obrigação legal / Legítimo interesse (Art. 7º, II e IX)'],
          ['Prevenção a fraudes', 'Legítimo interesse (Art. 7º, IX)'],
          ['Melhoria da plataforma e analytics do site (PostHog)', 'Consentimento (Art. 7º, I) — somente após aceite no banner'],
          ['Login social (Google / Apple)', 'Execução de contrato / Consentimento (Art. 7º, V e I)'],
        ],
      },
    ],
  },
  {
    id: 'compartilhamento',
    icon: '🤝',
    title: '4. Compartilhamento de Dados',
    content: [
      {
        type: 'highlight',
        value: 'Não vendemos, alugamos ou comercializamos seus dados pessoais a terceiros.',
      },
      {
        type: 'text',
        value: 'Compartilhamos dados apenas nas seguintes situações:',
      },
      {
        type: 'subsection',
        label: '4.1 Parceiros de Infraestrutura',
        items: [
          'Stripe Inc. — processamento de pagamentos (cartão e Pix, quando disponível), contas Stripe Connect para videomakers e gestão de escrow. Stripe tem sua própria política de privacidade (stripe.com/privacy)',
          'Amazon Web Services (AWS) — armazenamento de arquivos (S3), processamento de vídeos (MediaConvert) e distribuição via CloudFront. Os dados são armazenados em servidores com criptografia em repouso',
          'Apple Inc. — somente o token de identidade é recebido da Apple no processo de Sign in with Apple; não compartilhamos dados com a Apple além do necessário ao login',
          'Google LLC — autenticação via Google OAuth, quando você escolhe esse método de login',
          'PostHog — analytics do site ufokko.com.br somente se você aceitar cookies analíticos no banner de consentimento',
        ],
      },
      {
        type: 'subsection',
        label: '4.2 Entre Usuários da Plataforma',
        items: [
          'Nome e foto do videomaker são visíveis ao cliente durante o matching e a execução do job',
          'Nome e cidade do cliente são visíveis ao videomaker para aceitar o job',
          'Avaliações e comentários são públicos entre as partes do job',
          'Informações bancárias e dados completos de pagamento NUNCA são compartilhados entre usuários',
        ],
      },
      {
        type: 'subsection',
        label: '4.3 Obrigação Legal',
        items: [
          'Quando exigido por lei, regulamentação ou ordem judicial brasileira',
          'Para cooperar com autoridades competentes em investigações',
        ],
      },
    ],
  },
  {
    id: 'seguranca',
    icon: '🔒',
    title: '5. Segurança dos Dados',
    content: [
      {
        type: 'text',
        value:
          'Adotamos medidas técnicas e organizacionais para proteger seus dados contra acesso não autorizado, perda, alteração ou divulgação:',
      },
      {
        type: 'items',
        items: [
          'Criptografia de senhas com bcrypt (não armazenamos senhas em texto simples)',
          'Comunicação via HTTPS/TLS em todas as conexões',
          'Tokens JWT com expiração automática para autenticação',
          'Autenticação de dois fatores (2FA) obrigatória para administradores',
          'Armazenamento de arquivos na AWS S3 com criptografia em repouso (AES-256)',
          'URLs de acesso a arquivos privados via presigned URLs com tempo de expiração',
          'Logs de auditoria com dados de identificação mascarados (PII redaction)',
          'Acesso ao banco de dados restrito por IP e credenciais rotativas',
          'Monitoramento contínuo de atividades suspeitas',
          'Ambiente de produção separado do ambiente de testes',
        ],
      },
      {
        type: 'text',
        value:
          'Em caso de incidente de segurança que afete seus dados, notificaremos a Autoridade Nacional de Proteção de Dados (ANPD) e os titulares afetados nos prazos previstos pela LGPD.',
      },
    ],
  },
  {
    id: 'retencao',
    icon: '🗓️',
    title: '6. Retenção e Eliminação de Dados',
    content: [
      {
        type: 'text',
        value:
          'Mantemos seus dados pelo período necessário para as finalidades descritas nesta política:',
      },
      {
        type: 'subsection',
        label: '6.1 Durante a vigência da conta',
        items: [
          'Todos os dados cadastrais e de perfil são mantidos enquanto sua conta estiver ativa',
          'Histórico de jobs e entregas mantido para consulta e eventuais disputas',
          'Portfólio e avaliações mantidos para garantir a reputação e continuidade dos serviços',
        ],
      },
      {
        type: 'subsection',
        label: '6.2 Após exclusão da conta',
        items: [
          'Dados cadastrais são eliminados ou anonimizados em até 30 dias após a exclusão',
          'Dados financeiros (registros de transações) são mantidos por 5 anos para cumprimento de obrigações fiscais e legais',
          'Logs de auditoria são mantidos por 1 ano para fins de segurança',
          'Backups são eliminados conforme rotina de retenção (máximo 90 dias)',
        ],
      },
      {
        type: 'subsection',
        label: '6.3 Exclusão de conta',
        items: [
          'Você pode excluir sua conta diretamente no aplicativo: Menu → Perfil → Excluir minha conta',
          'Também é possível solicitar a exclusão via e-mail: contato@ufokko.com.br',
          'Jobs em andamento ou com pagamentos pendentes devem ser concluídos antes da exclusão',
        ],
      },
    ],
  },
  {
    id: 'direitos',
    icon: '⚖️',
    title: '7. Seus Direitos como Titular (LGPD)',
    content: [
      {
        type: 'text',
        value:
          'Nos termos da LGPD, você tem os seguintes direitos em relação aos seus dados pessoais:',
      },
      {
        type: 'rights',
        items: [
          { right: 'Acesso', desc: 'Solicitar confirmação e acesso a todos os dados que temos sobre você' },
          { right: 'Correção', desc: 'Corrigir dados incompletos, inexatos ou desatualizados — disponível diretamente no perfil do app' },
          { right: 'Anonimização ou Eliminação', desc: 'Solicitar a anonimização ou exclusão de dados desnecessários ou tratados em desconformidade com a LGPD' },
          { right: 'Portabilidade', desc: 'Solicitar a portabilidade dos seus dados a outro fornecedor, em formato estruturado e legível por máquina' },
          { right: 'Informação', desc: 'Ser informado sobre com quais entidades públicas e privadas seus dados foram compartilhados' },
          { right: 'Revogação do Consentimento', desc: 'Revogar consentimentos dados anteriormente, sem prejuízo dos tratamentos já realizados' },
          { right: 'Oposição', desc: 'Opor-se a tratamentos realizados com base em legítimo interesse que violem a LGPD' },
          { right: 'Exclusão de Conta', desc: 'Excluir completamente sua conta e dados no menu Perfil → Excluir minha conta' },
        ],
      },
      {
        type: 'info',
        label: 'Como exercer seus direitos',
        items: [
          'Via app: Menu → Perfil (para correção de dados e exclusão de conta)',
          'Via e-mail: contato@ufokko.com.br (assunto: "Direitos LGPD")',
          'Via DPO: dpo@ufokko.com.br',
          'Respondemos em até 15 dias úteis conforme previsto na LGPD',
        ],
      },
    ],
  },
  {
    id: 'pagamentos',
    icon: '💳',
    title: '8. Pagamentos e Escrow via Stripe',
    content: [
      {
        type: 'text',
        value:
          'A Ufokko utiliza o modelo de pagamento em escrow (custódia) via Stripe. Veja como funciona:',
      },
      {
        type: 'items',
        items: [
          'O cliente autoriza a cobrança no momento de confirmar o job, mas o valor não é imediatamente debitado',
          'O valor fica retido (pré-autorizado) pelo Stripe até a conclusão e aprovação do serviço',
          'Após o cliente aprovar as entregas, o valor é capturado e transferido ao videomaker via Stripe Connect',
          'A Ufokko retém uma taxa de plataforma sobre cada transação concluída',
          'Dados de cartão são inseridos e processados diretamente pelo Stripe — a Ufokko NUNCA tem acesso ao número completo do cartão ou CVV',
          'Videomakers recebem em sua conta bancária via Stripe Connect (conta Express), sujeita às políticas da Stripe',
        ],
      },
    ],
  },
  {
    id: 'menores',
    icon: '👤',
    title: '9. Restrição de Idade',
    content: [
      {
        type: 'highlight',
        value:
          'A plataforma Ufokko é destinada exclusivamente a maiores de 18 anos. Não coletamos intencionalmente dados de menores de idade.',
      },
      {
        type: 'text',
        value:
          'Se você acredita que dados de um menor de 18 anos foram inadvertidamente coletados, entre em contato imediatamente através de contato@ufokko.com.br para que possamos providenciar a exclusão.',
      },
    ],
  },
  {
    id: 'cookies',
    icon: '🍪',
    title: '10. Cookies e Tecnologias Similares',
    content: [
      {
        type: 'text',
        value:
          'O site ufokko.com.br utiliza cookies e tecnologias similares nos seguintes contextos. Você pode aceitar ou recusar analytics no banner exibido na primeira visita.',
      },
      {
        type: 'subsection',
        label: 'Essenciais / funcionais (não dependem de consentimento de marketing)',
        items: [
          'Preferência de tema (modo claro/escuro) — armazenada localmente no navegador',
          'Preferência de consentimento de analytics (aceito/recusado) — para lembrar sua escolha',
          'Sessão autenticada no painel web: tokens JWT em armazenamento local do navegador (não usamos cookies HttpOnly de sessão nesta versão)',
        ],
      },
      {
        type: 'subsection',
        label: 'Analíticos (somente com seu consentimento)',
        items: [
          'PostHog (us.i.posthog.com) — análise de uso, páginas visitadas e diagnóstico de erros na landing e no site',
          'Só são carregados após você clicar em “Aceitar” no banner de cookies',
          'Se recusar, o site continua funcionando; apenas não coletamos analytics de produto via PostHog',
        ],
      },
      {
        type: 'text',
        value:
          'O aplicativo móvel não utiliza cookies de navegador. A autenticação no app é gerenciada via tokens JWT armazenados de forma segura no dispositivo (SecureStore).',
      },
      {
        type: 'text',
        value:
          'Transferência internacional: o PostHog pode processar dados fora do Brasil; o tratamento ocorre apenas com consentimento e salvaguardas compatíveis com a LGPD (Art. 33).',
      },
    ],
  },
  {
    id: 'transferencia',
    icon: '🌎',
    title: '11. Transferência Internacional de Dados',
    content: [
      {
        type: 'text',
        value:
          'Alguns de nossos parceiros de infraestrutura estão localizados fora do Brasil, o que implica transferência internacional de dados:',
      },
      {
        type: 'items',
        items: [
          'AWS (Amazon Web Services): servidores na região us-east-1 (EUA). A AWS é certificada com padrões ISO 27001 e SOC 2, garantindo nível de proteção equivalente ao exigido pela LGPD',
          'Stripe Inc.: sede nos EUA, certificada PCI DSS Level 1, operando em conformidade com regulamentações internacionais de proteção de dados',
          'Apple Inc.: processamento do Sign in with Apple realizado nos termos da política de privacidade da Apple',
          'Google LLC: autenticação OAuth, conforme política de privacidade do Google',
          'PostHog: analytics do site (somente com consentimento), com infraestrutura que pode estar fora do Brasil',
        ],
      },
      {
        type: 'text',
        value:
          'Todas as transferências internacionais são realizadas com salvaguardas adequadas, conforme o Art. 33 da LGPD.',
      },
    ],
  },
  {
    id: 'alteracoes',
    icon: '🔄',
    title: '12. Alterações nesta Política',
    content: [
      {
        type: 'text',
        value:
          'Podemos atualizar esta Política de Privacidade periodicamente para refletir mudanças nos nossos serviços, práticas ou legislação aplicável.',
      },
      {
        type: 'items',
        items: [
          'Alterações significativas serão comunicadas via notificação no aplicativo e/ou e-mail com antecedência mínima de 15 dias',
          'A data da última atualização está sempre indicada no topo desta página',
          'A continuidade do uso da plataforma após as alterações implica aceite da nova versão',
          'Versões anteriores desta política podem ser solicitadas via contato@ufokko.com.br',
        ],
      },
    ],
  },
  {
    id: 'contato',
    icon: '📬',
    title: '13. Contato e Encarregado de Dados (DPO)',
    content: [
      {
        type: 'text',
        value:
          'Para qualquer questão relacionada a esta Política de Privacidade, ao tratamento dos seus dados ou ao exercício dos seus direitos como titular, entre em contato:',
      },
      {
        type: 'info',
        label: 'Canais de contato',
        items: [
          'E-mail geral: contato@ufokko.com.br',
          'DPO (Encarregado de Dados — LGPD): dpo@ufokko.com.br',
          'Exclusão de conta via app: Perfil → Excluir minha conta',
          'Localização: Rio de Janeiro, RJ, Brasil',
          'Prazo de resposta: até 15 dias úteis',
        ],
      },
      {
        type: 'text',
        value:
          'Você também pode registrar reclamações perante a Autoridade Nacional de Proteção de Dados (ANPD) em gov.br/anpd, caso considere que seus direitos não foram atendidos.',
      },
    ],
  },
];

// ─── Sub-componentes de renderização ─────────────────────────────────────────

const SectionContent = ({ content }) => (
  <div className="space-y-4">
    {content.map((block, i) => {
      switch (block.type) {
        case 'text':
          return (
            <p key={i} className="text-gray-700 dark:text-gray-300 leading-relaxed">
              {block.value}
            </p>
          );

        case 'highlight':
          return (
            <div key={i} className="bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500 px-4 py-3 rounded-r-lg">
              <p className="text-blue-800 dark:text-blue-200 font-medium leading-relaxed">
                {block.value}
              </p>
            </div>
          );

        case 'info':
          return (
            <div key={i} className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
              <p className="font-semibold text-gray-900 dark:text-white mb-2 text-sm uppercase tracking-wide">
                {block.label}
              </p>
              <ul className="space-y-1">
                {block.items.map((item, j) => (
                  <li key={j} className="text-gray-700 dark:text-gray-300 text-sm flex gap-2">
                    <span className="text-blue-500 mt-0.5 shrink-0">›</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          );

        case 'subsection':
          return (
            <div key={i} className="mt-2">
              <p className="font-semibold text-gray-900 dark:text-white mb-2">{block.label}</p>
              <ul className="space-y-1.5 ml-4">
                {block.items.map((item, j) => (
                  <li key={j} className="text-gray-700 dark:text-gray-300 flex gap-2">
                    <span className="text-primary mt-1 shrink-0">•</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          );

        case 'items':
          return (
            <ul key={i} className="space-y-2">
              {block.items.map((item, j) => (
                <li key={j} className="text-gray-700 dark:text-gray-300 flex gap-2">
                  <span className="text-green-500 mt-1 shrink-0">✓</span>
                  {item}
                </li>
              ))}
            </ul>
          );

        case 'rights':
          return (
            <div key={i} className="grid sm:grid-cols-2 gap-3 mt-2">
              {block.items.map((item, j) => (
                <div key={j} className="bg-blue-50 dark:bg-primary/20 rounded-xl p-3 border border-blue-100 dark:border-primary/30">
                  <p className="font-semibold text-primary dark:text-blue-300 text-sm mb-1">
                    {item.right}
                  </p>
                  <p className="text-gray-600 dark:text-gray-400 text-sm leading-snug">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          );

        case 'table':
          return (
            <div key={i} className="overflow-x-auto mt-2 rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100 dark:bg-gray-700">
                    {block.rows[0].map((cell, j) => (
                      <th key={j} className="px-4 py-2 text-left font-semibold text-gray-800 dark:text-gray-200">
                        {cell}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.slice(1).map((row, j) => (
                    <tr key={j} className={j % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-750'}>
                      {row.map((cell, k) => (
                        <td key={k} className="px-4 py-2 text-gray-700 dark:text-gray-300 border-t border-gray-100 dark:border-gray-700">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );

        default:
          return null;
      }
    })}
  </div>
);

// ─── Componente principal ─────────────────────────────────────────────────────

const PoliticaPrivacidade = () => {
  const navigate = useNavigate();
  const [policyMeta, setPolicyMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null);

  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        // Cliente axios: baseURL via runtime-config + unwrap do envelope StandardResponse.
        const { data } = await api.get('/lgpd/privacy-policy');
        // Preferir meta do FE se a API ainda estiver em versão antiga (< 2.1)
        const apiVersion = data?.version || '0';
        const [maj, min] = apiVersion.split('.').map((n) => parseInt(n, 10) || 0);
        if (maj > 2 || (maj === 2 && min >= 1)) {
          setPolicyMeta({ version: data.version, last_updated: data.last_updated });
        } else {
          setPolicyMeta(STATIC_POLICY);
        }
      } catch {
        setPolicyMeta(STATIC_POLICY);
      } finally {
        setLoading(false);
      }
    };
    fetchPolicy();
  }, []);

  // Scroll spy
  useEffect(() => {
    const handler = () => {
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i].id);
        if (el && el.getBoundingClientRect().top <= 120) {
          setActiveSection(sections[i].id);
          return;
        }
      }
      setActiveSection(null);
    };
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const formattedDate = policyMeta
    ? new Date(policyMeta.last_updated).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    : '—';

  return (
    <div className="min-h-full">
      <PageSeo
        title="Política de Privacidade | Ufokko"
        description="Política de privacidade e tratamento de dados pessoais da Ufokko em conformidade com a LGPD (Lei 13.709/2018)."
        path="/politica-de-privacidade"
      />
      <div className="container mx-auto px-4 py-10 max-w-6xl">
        <div className="lg:grid lg:grid-cols-[280px_1fr] lg:gap-10 lg:items-start">

          {/* Sidebar: Sumário */}
          <aside className="hidden lg:block sticky top-20 self-start">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">
                Índice
              </p>
              <nav className="space-y-1">
                {sections.map((sec) => (
            <button 
                    key={sec.id}
                    onClick={() => scrollTo(sec.id)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${
                      activeSection === sec.id
                        ? 'bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 font-medium'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <span className="text-base shrink-0">{sec.icon}</span>
                    <span className="leading-tight">
                      {sec.title.replace(/^\d+\.\s/, '')}
                    </span>
            </button>
                ))}
              </nav>
          </div>
          </aside>

          {/* Conteúdo principal */}
          <main>
            {/* Hero */}
        <motion.div
              initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mb-10"
            >
              <div className="flex items-center gap-3 mb-2">
                <span className="text-3xl">🔐</span>
                <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white">
                  Política de Privacidade
                </h1>
              </div>
              <p className="text-gray-500 dark:text-gray-400 mt-1 ml-12">
                Em conformidade com a{' '}
                <strong className="text-gray-700 dark:text-gray-300">LGPD — Lei nº 13.709/2018</strong>
              </p>

              {loading ? (
                <div className="ml-12 mt-2 h-4 w-48 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              ) : (
                <div className="ml-12 mt-2 flex flex-wrap gap-3 text-sm">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded-full font-medium">
                    📌 Versão {policyMeta?.version || '2.0'}
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-full">
                    🗓️ Atualizada em {formattedDate}
                  </span>
                </div>
              )}
            </motion.div>

            {/* Seções */}
            <div className="space-y-6">
              {sections.map((sec, idx) => (
                <motion.section
                  key={sec.id}
                  id={sec.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: idx * 0.04 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 scroll-mt-24"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl">{sec.icon}</span>
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      {sec.title}
                    </h2>
                </div>
                  <SectionContent content={sec.content} />
                </motion.section>
              ))}
              </div>

            {/* Rodapé do documento */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-8 p-6 bg-gradient-to-r from-[#0E76FF] to-[#2563EB] rounded-2xl text-white"
            >
              <h3 className="text-lg font-bold mb-2">Tem dúvidas sobre sua privacidade?</h3>
              <p className="text-blue-100 text-sm mb-4">
                Entre em contato com nosso Encarregado de Proteção de Dados (DPO) ou com o time de suporte.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="mailto:dpo@ufokko.com.br"
                  className="px-4 py-2 bg-white text-blue-700 rounded-full font-semibold text-sm hover:shadow-lg transition-shadow"
                >
                  📧 dpo@ufokko.com.br
                </a>
                <a
                  href="mailto:contato@ufokko.com.br"
                  className="px-4 py-2 bg-white/20 border border-white/40 text-white rounded-full font-semibold text-sm hover:bg-white/30 transition-colors"
                >
                  📬 contato@ufokko.com.br
                </a>
              </div>
            </motion.div>

            <div className="mt-6 text-center">
              <button
                onClick={() => navigate('/')}
                className="px-6 py-3 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 rounded-full font-semibold hover:opacity-80 transition-opacity text-sm"
              >
                ← Voltar para a página inicial
              </button>
            </div>
      </main>
        </div>
      </div>
    </div>
  );
};

export default PoliticaPrivacidade;
